// =============================================================================
// Normascan — Plateforme d'analyse normative par IA
// Copyright (c) 2026-2027 Evalens SAS — Tous droits réservés
// Marque Normascan déposée INPI n°5242986 (classes 9 et 42)
// Site : https://app.normascan.fr
// =============================================================================
//
// Consultation du journal d'audit (5 août 2026).
//
// La table `audit_logs` comptait **plus de 60 points d'écriture et aucune
// lecture** : connexions, révocations, changements de rôle, factures et
// effacements RGPD y étaient consignés depuis avril 2026, mais le seul moyen de
// les consulter était `psql` sur le VPS. Une trace qu'on ne peut pas lire ne
// sert qu'après coup, et seulement à qui a les clés de la base.
//
// ⚠️ La consultation n'est volontairement PAS auditée : la tracer ferait
// grossir la table à chaque page tournée et noierait les écritures qui comptent.
import React, { useState, useEffect, useCallback } from 'react';
import { getAuditLogs, getAuditLogActions } from '../services/api';
import { useI18n } from '../i18n';
import SkeletonRows from '../components/SkeletonRows';

const PAGE_SIZE = 50;

// Les actions les plus sensibles ressortent visuellement. En incident on cherche
// une élévation de privilège ou un effacement, pas une connexion réussie parmi
// dix mille — sans repère, l'œil ne les distingue pas dans un mur de texte.
const COULEURS_ACTION = [
  [/DELETED|DELETE|REVOKED/,          { bg: '#fee2e2', text: '#991b1b' }],
  [/UPDATED|RIGHTS|ROLE|RESET|MFA/,   { bg: '#fef3c7', text: '#92400e' }],
  [/FAILED|REFUSED|DENIED|BRUTEFORCE/,{ bg: '#fce7f3', text: '#9d174d' }],
  [/INVOICE|EXPORT/,                  { bg: '#e0f2fe', text: '#0369a1' }],
];

const styleAction = (action) => {
  const trouve = COULEURS_ACTION.find(([motif]) => motif.test(action));
  const c = trouve ? trouve[1] : { bg: '#f3f4f6', text: 'var(--text-muted)' };
  return {
    background: c.bg, color: c.text, fontSize: 11, fontWeight: 600,
    padding: '2px 8px', borderRadius: 99, whiteSpace: 'nowrap',
  };
};

const FILTRES_VIDES = {
  actions: '', email: '', q: '', date_from: '', date_to: '', status: '',
};

const champ = {
  padding: '6px 9px', borderRadius: 6, border: '1px solid var(--gray)',
  fontSize: 12, background: 'var(--bg-input)', color: 'var(--black)',
  boxSizing: 'border-box', outline: 'none',
};

const AdminAuditLogs = () => {
  const { t, lang } = useI18n();
  const [filtres, setFiltres]     = useState(FILTRES_VIDES);
  const [logs, setLogs]           = useState([]);
  const [total, setTotal]         = useState(0);
  const [offset, setOffset]       = useState(0);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur]       = useState('');
  const [actionsDispo, setActionsDispo] = useState([]);

  // Chargée une seule fois : c'est un `SELECT DISTINCT` sans index, inutile de
  // le rejouer à chaque page tournée.
  useEffect(() => {
    getAuditLogActions()
      .then(d => setActionsDispo(d.actions || []))
      .catch(() => setActionsDispo([]));
  }, []);

  const charger = useCallback(async (decalage, f) => {
    setChargement(true); setErreur('');
    try {
      const d = await getAuditLogs({ ...f, limit: PAGE_SIZE, offset: decalage });
      setLogs(d.logs || []);
      setTotal(d.total || 0);
    } catch (e) { setErreur(e.message); setLogs([]); }
    finally { setChargement(false); }
  }, []);

  useEffect(() => { charger(0, FILTRES_VIDES); }, [charger]);

  // Tout changement de filtre ramène en page 1 : garder l'offset donnerait une
  // page vide sur un résultat plus court, ce qui se lit comme « aucun événement ».
  const appliquer = (nouveaux) => {
    setFiltres(nouveaux);
    setOffset(0);
    charger(0, nouveaux);
  };

  const majFiltre = (cle, valeur) => appliquer({ ...filtres, [cle]: valeur });

  const tourner = (nouveauDecalage) => {
    setOffset(nouveauDecalage);
    charger(nouveauDecalage, filtres);
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString(lang === 'en' ? 'en-GB' : 'fr-FR', {
        day: '2-digit', month: '2-digit', year: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
      });
    } catch { return iso; }
  };

  const page = Math.floor(offset / PAGE_SIZE) + 1;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div style={{ flex: 1, overflowY: 'auto', background: '#f9fafb' }}>
      <div style={{ maxWidth: 1400, margin: '0 auto', padding: '32px 24px' }}>
        <h1 style={{ fontFamily: 'Futura, Montserrat, sans-serif', fontSize: 24, fontWeight: 700, color: 'var(--black)', marginBottom: 6 }}>
          🔎 {t('audit_title')}
        </h1>
        <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 20 }}>
          {t('audit_intro')}
        </p>

        {/* Filtres */}
        <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--gray)', borderRadius: 10, padding: '14px 16px', marginBottom: 16, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <label style={{ fontSize: 11, color: 'var(--text-muted)', display: 'block', marginBottom: 3 }}>{t('audit_filter_action')}</label>
            <select value={filtres.actions} onChange={e => majFiltre('actions', e.target.value)} style={{ ...champ, minWidth: 200 }}>
              <option value="">{t('audit_filter_all_actions')}</option>
              {actionsDispo.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 11, color: 'var(--text-muted)', display: 'block', marginBottom: 3 }}>{t('audit_filter_email')}</label>
            <input value={filtres.email} onChange={e => setFiltres({ ...filtres, email: e.target.value })}
              onBlur={() => appliquer(filtres)}
              onKeyDown={e => e.key === 'Enter' && appliquer(filtres)}
              placeholder={t('audit_filter_email_ph')} style={{ ...champ, minWidth: 190 }} />
          </div>
          <div>
            <label style={{ fontSize: 11, color: 'var(--text-muted)', display: 'block', marginBottom: 3 }}>{t('audit_filter_search')}</label>
            <input value={filtres.q} onChange={e => setFiltres({ ...filtres, q: e.target.value })}
              onBlur={() => appliquer(filtres)}
              onKeyDown={e => e.key === 'Enter' && appliquer(filtres)}
              placeholder={t('audit_filter_search_ph')} style={{ ...champ, minWidth: 220 }} />
          </div>
          <div>
            <label style={{ fontSize: 11, color: 'var(--text-muted)', display: 'block', marginBottom: 3 }}>{t('audit_filter_from')}</label>
            <input type="date" value={filtres.date_from} onChange={e => majFiltre('date_from', e.target.value)} style={champ} />
          </div>
          <div>
            <label style={{ fontSize: 11, color: 'var(--text-muted)', display: 'block', marginBottom: 3 }}>{t('audit_filter_to')}</label>
            <input type="date" value={filtres.date_to} onChange={e => majFiltre('date_to', e.target.value)} style={champ} />
          </div>
          <div>
            <label style={{ fontSize: 11, color: 'var(--text-muted)', display: 'block', marginBottom: 3 }}>{t('audit_filter_status')}</label>
            <select value={filtres.status} onChange={e => majFiltre('status', e.target.value)} style={champ}>
              <option value="">{t('audit_filter_all_status')}</option>
              <option value="success">success</option>
              <option value="failure">failure</option>
            </select>
          </div>
          <button onClick={() => appliquer(FILTRES_VIDES)}
            style={{ ...champ, cursor: 'pointer', fontWeight: 600, color: 'var(--text-muted)' }}>
            {t('audit_reset')}
          </button>
        </div>

        {erreur && <p style={{ color: '#ef4444', fontSize: 13, marginBottom: 12 }}>❌ {erreur}</p>}

        <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--gray)', borderRadius: 10, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--gray)' }}>
                  {[t('audit_col_date'), t('audit_col_action'), t('audit_col_author'),
                    t('audit_col_details'), t('audit_col_ip'), t('audit_col_status')].map((h, i) => (
                    <th key={i} style={{ padding: '9px 12px', textAlign: 'left', fontWeight: 600, fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {chargement && <SkeletonRows rows={8} cols={6} />}
                {!chargement && logs.length === 0 && (
                  <tr><td colSpan={6} style={{ padding: '24px 12px', textAlign: 'center', color: 'var(--text-muted)' }}>{t('audit_empty')}</td></tr>
                )}
                {!chargement && logs.map(l => (
                  <tr key={l.id} style={{ borderBottom: '1px solid var(--gray)' }}>
                    <td style={{ padding: '8px 12px', whiteSpace: 'nowrap', color: 'var(--text-muted)' }}>{formatDate(l.timestamp)}</td>
                    <td style={{ padding: '8px 12px' }}><span style={styleAction(l.action)}>{l.action}</span></td>
                    <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>{l.user_email || '—'}</td>
                    {/* `break-word` : un détail peut porter un diff long
                        (« role: user → admin ; quota_max: 10 → 250 ») et ne doit
                        pas étirer le tableau au point de masquer les colonnes. */}
                    <td style={{ padding: '8px 12px', color: 'var(--text-muted)', maxWidth: 460, wordBreak: 'break-word' }}>{l.details || '—'}</td>
                    <td style={{ padding: '8px 12px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{l.ip_address || '—'}</td>
                    <td style={{ padding: '8px 12px' }}>
                      <span style={{
                        fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 99,
                        background: l.status === 'success' ? '#f0fdf4' : '#fee2e2',
                        color: l.status === 'success' ? '#16a34a' : '#991b1b',
                      }}>{l.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 14 }}>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            {total} {t('audit_count')} · {t('audit_page')} {page}/{pages}
          </span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => tourner(Math.max(0, offset - PAGE_SIZE))}
              disabled={offset === 0 || chargement}
              style={{ ...champ, cursor: offset === 0 ? 'not-allowed' : 'pointer', opacity: offset === 0 ? 0.5 : 1 }}>
              {t('audit_prev')}
            </button>
            <button onClick={() => tourner(offset + PAGE_SIZE)}
              disabled={offset + PAGE_SIZE >= total || chargement}
              style={{ ...champ, cursor: offset + PAGE_SIZE >= total ? 'not-allowed' : 'pointer', opacity: offset + PAGE_SIZE >= total ? 0.5 : 1 }}>
              {t('audit_next')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAuditLogs;
